<script setup>
import { ref, computed, watch } from 'vue'
import { Head, router, useForm, usePage } from '@inertiajs/vue3'
import { PhUserCirclePlus, PhPrinter } from '@phosphor-icons/vue'
import Layout   from '@pages/Shared/layouts/StaffLayout.vue'
import Table    from '@pages/Shared/ui/Table.vue'
import Modal    from '@pages/Shared/Modal.vue'
import Input    from '@pages/Shared/form/Input.vue'
import Select   from '@pages/Shared/form/Select.vue'
import Textarea from '@pages/Shared/form/Textarea.vue'
import Spinner  from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'
import { useAdmissionStore } from '@/Stores/useAdmissionStore'

const props = defineProps({
    applications:     Object,
    manualAdmissions: Object,
    sessions:         Array,
    sections:         Array,
    activeSession:    Object,
    counts:           Object,
    statuses:         Array,
    filters:          Object,
})

const page           = usePage()
const admissionStore = useAdmissionStore()

// ── Filters ────────────────────────────────────────────────────────────────
const filters = ref({
    search: null, session_id: null, section_id: null, status: null,
    ...props.filters,
})
watch(() => props.filters, val => { filters.value = { ...val } }, { deep: true })

const applyFilters = () => {
    const cleaned = Object.fromEntries(
        Object.entries(filters.value).filter(([_, v]) => v !== null && v !== undefined && v !== '')
    )
    router.get('/staff/manage-admissions', cleaned, {
        preserveState: true, preserveScroll: true, replace: true,
    })
}
const handleSearch = v => { filters.value.search = v; applyFilters() }
const handleFilter = f => { filters.value = { ...filters.value, ...f }; applyFilters() }

// ── Manual Admission Modal ─────────────────────────────────────────────────
const manualModal = ref(false)
const manualForm  = useForm({
    academic_session_id: props.activeSession?.id ?? null,
    class_room_id:       null,
    first_name: '', last_name: '', other_name: '',
    date_of_birth: '', gender: null, religion: '',
    nationality: 'Nigerian', state_of_origin: '', lga_of_origin: '',
    address: '', phone: '', email: '',
    guardian_name: '', guardian_phone: '', guardian_relationship: '', guardian_address: '',
    previous_school: '', previous_class: '', notes: '',
})

const submitManual = () => {
    manualForm.post('/staff/manage-admissions/manual', {
        onSuccess: page => {
            manualModal.value = false
            manualForm.reset()
            flashToast(page.props.flash)
        },
    })
}

// ── Options ────────────────────────────────────────────────────────────────
const sectionOptions = computed(() =>
    (props.sections ?? []).map(s => ({ label: s.name, value: s.id }))
)
const sessionOptions = computed(() =>
    (props.sessions ?? []).map(s => ({ label: s.name, value: s.id }))
)
const classOptions = computed(() =>
    (props.sections ?? []).flatMap(s =>
        (s.classes ?? []).map(c => ({ label: `${c.name} (${s.name})`, value: c.id }))
    )
)

// ── Helpers ────────────────────────────────────────────────────────────────
const statusColor = s => ({
    draft:       'bg-gray-100 text-gray-600',
    submitted:   'bg-primary-50 text-primary-700',
    invited:     'bg-indigo-50 text-indigo-700',
    interviewed: 'bg-purple-50 text-purple-700',
    admitted:    'bg-green-50 text-green-700',
    rejected:    'bg-red-50 text-red-600',
    enrolled:    'bg-emerald-50 text-emerald-700',
}[s] ?? 'bg-gray-100 text-gray-600')

// ── Table columns ──────────────────────────────────────────────────────────
const appColumns = [
    { key: 'index',     label: '#',             type: 'index' },
    { key: 'applicant', label: 'Applicant',     type: 'slot'  },
    { key: 'section',   label: 'Section',       type: 'slot'  },
    { key: 'status',    label: 'Status',        type: 'slot'  },
    { key: 'admission', label: 'Admission No.', type: 'slot'  },
    { key: 'enrolled',  label: 'Enrolled',      type: 'slot'  },
    { key: 'actions',   label: '',              type: 'slot'  },
]

const manualColumns = [
    { key: 'index',    label: '#',             type: 'index' },
    { key: 'name',     label: 'Name',          type: 'slot'  },
    { key: 'class',    label: 'Class',         type: 'slot'  },
    { key: 'adm_no',   label: 'Admission No.', type: 'slot'  },
    { key: 'enrolled', label: 'Enrolled',      type: 'slot'  },
    { key: 'admitted', label: 'Admitted',      type: 'slot'  },
    { key: 'actions',  label: '',              type: 'slot'  },
]

const activeTab = ref('online')
</script>

<template>
    <Layout>
        <Head title="Admissions" />

        <!-- ── Manual Admission Modal ── -->
        <Modal v-model="manualModal" title="Add Walk-in Admission" @close="manualModal = false">
            <form class="p-4 md:p-5 space-y-4 max-h-[75vh] overflow-y-auto" @submit.prevent="submitManual">
                <div class="grid grid-cols-2 gap-4">
                    <Select label="Session" v-model="manualForm.academic_session_id"
                        :options="sessionOptions" placeholder="Select session"
                        :error="manualForm.errors.academic_session_id" />
                    <Select label="Class" v-model="manualForm.class_room_id"
                        :options="classOptions" placeholder="Select class"
                        :error="manualForm.errors.class_room_id" />
                    <Input label="First Name"    v-model="manualForm.first_name"    :error="manualForm.errors.first_name" />
                    <Input label="Last Name"     v-model="manualForm.last_name"     :error="manualForm.errors.last_name" />
                    <Input label="Other Name"    v-model="manualForm.other_name" />
                    <Input label="Date of Birth" v-model="manualForm.date_of_birth" type="date"
                        :error="manualForm.errors.date_of_birth" />
                    <Select label="Gender" v-model="manualForm.gender"
                        :options="[{ label: 'Male', value: 'male' }, { label: 'Female', value: 'female' }]"
                        placeholder="Select gender" />
                    <Input label="Religion"     v-model="manualForm.religion" />
                    <Input label="Phone"        v-model="manualForm.phone" />
                    <Input label="Email"        v-model="manualForm.email" type="email" />
                </div>
                <Textarea label="Address" v-model="manualForm.address" :rows="2" />

                <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Guardian</p>
                <div class="grid grid-cols-2 gap-4">
                    <Input label="Guardian Name"  v-model="manualForm.guardian_name" />
                    <Input label="Guardian Phone" v-model="manualForm.guardian_phone" />
                    <Input label="Relationship"   v-model="manualForm.guardian_relationship" />
                </div>

                <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">Previous School</p>
                <div class="grid grid-cols-2 gap-4">
                    <Input label="School Name" v-model="manualForm.previous_school" />
                    <Input label="Class"       v-model="manualForm.previous_class" />
                </div>

                <Textarea label="Notes" v-model="manualForm.notes" :rows="2" />

                <button type="submit" :disabled="manualForm.processing"
                    class="w-full py-3 rounded-lg bg-primary hover:bg-primary-light text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition">
                    <Spinner v-if="manualForm.processing" :size="14" />
                    Create Admission
                </button>
            </form>
        </Modal>

        <!-- ── Header ── -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h2 class="text-xl font-bold text-primary">Admissions</h2>
                <p class="text-sm text-gray-500 mt-0.5">Online applications and manual walk-in admissions</p>
            </div>
            <button
                @click="manualModal = true"
                class="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                       text-white bg-primary hover:bg-primary-light rounded-lg transition">
                <PhUserCirclePlus :size="20" />
                Add Walk-in
            </button>
        </div>

        <!-- ── Tabs ── -->
        <div class="flex items-center gap-1 mb-5 border-b border-gray-200">
            <button
                v-for="tab in ['online', 'manual']"
                :key="tab"
                @click="activeTab = tab"
                class="px-5 py-2.5 text-sm font-semibold capitalize transition border-b-2 -mb-px"
                :class="activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'"
            >
                {{ tab === 'online' ? 'Online Applications' : 'Manual / Walk-in' }}
            </button>
        </div>

        <!-- ── Online Applications ── -->
        <template v-if="activeTab === 'online'">
            <div class="flex items-center gap-2 mb-4 flex-wrap">
                <button
                    v-for="s in [{ value: null, label: 'All' }, ...statuses.map(s => ({ value: s.value, label: s.value }))]"
                    :key="s.value"
                    @click="filters.status = s.value; applyFilters()"
                    class="px-3 py-1 rounded-lg text-xs font-semibold border transition capitalize"
                    :class="filters.status === s.value
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-primary'"
                >
                    {{ s.label }}
                    <span v-if="s.value && counts?.[s.value]" class="ml-1 text-[10px]">
                        {{ counts[s.value] }}
                    </span>
                </button>
            </div>

            <Table :data="applications" :columns="appColumns" row-key="id"
                :actions="{ view: true }"
                :filters="[
                    { key: 'session_id', label: 'Session',
                      options: [{ label: 'All Sessions', value: null }, ...sessionOptions],
                      value: filters.session_id },
                    { key: 'section_id', label: 'Section',
                      options: [{ label: 'All Sections', value: null }, ...sectionOptions],
                      value: filters.section_id },
                ]"
                @search="handleSearch"
                @filter="handleFilter"
                @view="id => router.get(`/staff/manage-admissions/${id}`)">

                <template #applicant="{ row }">
                    <div>
                        <p class="text-sm font-medium text-gray-800">
                            {{ row.applicant?.first_name }} {{ row.applicant?.last_name }}
                        </p>
                        <p class="text-xs text-gray-400">{{ row.applicant?.id }}</p>
                    </div>
                </template>

                <template #section="{ row }">
                    <span class="text-sm text-gray-600">{{ row.section?.name ?? '—' }}</span>
                </template>

                <template #status="{ row }">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
                        :class="statusColor(row.status)">
                        {{ row.status }}
                    </span>
                </template>

                <template #admission="{ row }">
                    <span class="text-sm font-mono text-gray-700">
                        {{ row.admission?.admission_number ?? '—' }}
                    </span>
                </template>

                <template #enrolled="{ row }">
                    <span :class="row.admission?.student_id
                        ? 'text-green-600 text-xs font-medium'
                        : 'text-gray-400 text-xs'">
                        {{ row.admission?.student_id ? 'Yes' : 'No' }}
                    </span>
                </template>

                <template #actions="{ row }">
                    <button
                        v-if="row.admission && ['admitted', 'enrolled'].includes(row.status)"
                        @click="admissionStore.printAdmissionLetter(row.admission)"
                        :disabled="admissionStore.isGenerating"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold
                               rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200
                               transition disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Print Admission Letter"
                    >
                        <PhPrinter :size="13" />
                        Print
                    </button>
                </template>
            </Table>
        </template>

        <!-- ── Manual Admissions ── -->
        <template v-if="activeTab === 'manual'">
            <Table :data="manualAdmissions" :columns="manualColumns" row-key="id"
                :actions="{ view: true }"
                :filters="[
                    { key: 'class_id', label: 'Class',
                      options: [{ label: 'All Classes', value: null }, ...classOptions],
                      value: filters.class_id },
                ]"
                @search="handleSearch"
                @filter="handleFilter"
                @view="id => router.get(`/staff/manage-admissions/${id}`)">

                <template #name="{ row }">
                    <p class="text-sm font-medium text-gray-800">
                        {{ row.first_name }} {{ row.last_name }}
                    </p>
                </template>

                <template #class="{ row }">
                    <span class="text-sm text-gray-600">{{ row.class_room?.name ?? '—' }}</span>
                </template>

                <template #adm_no="{ row }">
                    <span class="text-sm font-mono text-gray-700">
                        {{ row.admission_number ?? '—' }}
                    </span>
                </template>

                <template #enrolled="{ row }">
                    <span :class="row.student_id
                        ? 'text-green-600 text-xs font-medium'
                        : 'text-gray-400 text-xs'">
                        {{ row.student_id ? 'Yes' : 'No' }}
                    </span>
                </template>

                <template #admitted="{ row }">
                    <span class="text-xs text-gray-500">
                        {{ row.admitted_at ? new Date(row.admitted_at).toLocaleDateString() : '—' }}
                    </span>
                </template>

                <template #actions="{ row }">
                    <button
                        @click="admissionStore.printAdmissionLetter(row)"
                        :disabled="admissionStore.isGenerating"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold
                               rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200
                               transition disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Print Admission Letter"
                    >
                        <PhPrinter :size="13" />
                        Print
                    </button>
                </template>
            </Table>
        </template>
    </Layout>
</template>